import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Check } from 'lucide-react';

interface PunishmentWitnessTabProps {
  formData: any;
  handleChange: (field: string, value: any) => void;
  employees: any[];
  readonly?: boolean;
}

// Multi-Witness Input Component
function MultiWitnessInput({
  value,
  onChange,
  placeholder,
  employees,
  readonly = false,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  employees: any[];
  readonly?: boolean;
}) {
  const [inputValue, setInputValue] = useState<string>('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Parse witness string to array (comma-separated)
  const witnesses = useMemo(() => {
    if (!value || value.trim() === '') return [];
    return value
      .split(',')
      .map(w => w.trim())
      .filter(w => w.length > 0);
  }, [value]);

  // Filter employees based on input
  useEffect(() => {
    if (inputValue && inputValue.trim().length > 0 && !readonly) {
      const searchTerm = inputValue.toLowerCase().trim();
      const filtered = employees
        .map(emp => emp.full_name)
        .filter(name => {
          const lowerName = name.toLowerCase();
          return lowerName.includes(searchTerm) && !witnesses.includes(name);
        })
        .slice(0, 5); // Limit to 5 suggestions
      setSuggestions(filtered);
      setShowSuggestions(filtered.length > 0 && searchTerm.length > 0);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [inputValue, employees, readonly, witnesses]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const addWitness = (name: string) => {
    const trimmedName = name.trim();
    if (trimmedName && !witnesses.includes(trimmedName)) {
      const newWitnesses = [...witnesses, trimmedName];
      onChange(newWitnesses.join(', '));
      setInputValue('');
      setShowSuggestions(false);
    }
  };

  const removeWitness = (name: string) => {
    const newWitnesses = witnesses.filter(w => w !== name);
    onChange(newWitnesses.join(', '));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (inputValue.trim()) {
        addWitness(inputValue);
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const handleSelectSuggestion = (name: string) => {
    addWitness(name);
  };

  const calculateSuggestionsPosition = () => {
    if (!inputRef.current) return null;
    const rect = inputRef.current.getBoundingClientRect();
    return {
      position: 'fixed' as const,
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    };
  };

  // If readonly mode, only show witness tags
  if (readonly) {
    return (
      <div className="space-y-2">
        {witnesses.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {witnesses.map((witness, index) => (
              <div
                key={index}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-md border border-blue-200"
              >
                <span className="text-sm font-medium">{witness}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 italic">Tidak ada saksi</p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="relative flex items-center gap-2">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => {
            if (suggestions.length > 0) {
              setShowSuggestions(true);
            }
          }}
          onKeyDown={handleKeyDown}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
          placeholder={placeholder}
          autoComplete="off"
        />
        {inputValue.trim() && (
          <button
            type="button"
            onClick={() => addWitness(inputValue)}
            className="flex-shrink-0 p-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors"
            title="Tambah saksi"
          >
            <Check className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Witness Tags */}
      {witnesses.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {witnesses.map((witness, index) => (
            <div
              key={index}
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-md border border-blue-200"
            >
              <span className="text-sm font-medium">{witness}</span>
              <button
                type="button"
                onClick={() => removeWitness(witness)}
                className="flex-shrink-0 p-0.5 hover:bg-blue-100 rounded transition-colors"
                title="Hapus saksi"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Suggestions Dropdown */}
      {showSuggestions &&
        suggestions.length > 0 &&
        calculateSuggestionsPosition() &&
        createPortal(
          <div
            ref={suggestionsRef}
            style={calculateSuggestionsPosition()}
            className="z-[99999] bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto animate-dropdown-in"
          >
            {suggestions.map((name, index) => (
              <div
                key={index}
                onClick={() => handleSelectSuggestion(name)}
                className="w-full px-3 py-2 text-left hover:bg-gray-50 cursor-pointer text-gray-700"
              >
                {name}
              </div>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
}

// Autocomplete Input Component
function AutocompleteInput({
  value,
  onChange,
  placeholder,
  fieldName,
  employees,
  readonly = false,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  fieldName: string;
  employees: any[];
  readonly?: boolean;
}) {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Filter employees based on input
  useEffect(() => {
    if (value && value.trim().length > 0 && !readonly) {
      const searchTerm = value.toLowerCase().trim();
      const filtered = employees
        .map(emp => emp.full_name)
        .filter(name => name.toLowerCase().includes(searchTerm))
        .slice(0, 5); // Limit to 5 suggestions
      setSuggestions(filtered);
      setShowSuggestions(filtered.length > 0 && searchTerm.length > 0);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [value, employees, readonly]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  const handleSelectSuggestion = (name: string) => {
    onChange(name);
    setShowSuggestions(false);
  };

  const calculateSuggestionsPosition = () => {
    if (!inputRef.current) return null;
    const rect = inputRef.current.getBoundingClientRect();
    return {
      position: 'fixed' as const,
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    };
  };

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={value || ''}
        onChange={handleChange}
        onFocus={() => {
          if (suggestions.length > 0) {
            setShowSuggestions(true);
          }
        }}
        onKeyDown={e => {
          if (e.key === 'Escape') {
            setShowSuggestions(false);
          }
        }}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
        placeholder={placeholder}
        disabled={readonly}
        autoComplete="off"
      />
      {showSuggestions &&
        suggestions.length > 0 &&
        calculateSuggestionsPosition() &&
        createPortal(
          <div
            ref={suggestionsRef}
            style={calculateSuggestionsPosition()}
            className="z-[99999] bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto animate-dropdown-in"
          >
            {suggestions.map((name, index) => (
              <div
                key={index}
                onClick={() => handleSelectSuggestion(name)}
                className="w-full px-3 py-2 text-left hover:bg-gray-50 cursor-pointer text-gray-700"
              >
                {name}
              </div>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
}

export default function PunishmentWitnessTab({
  formData,
  handleChange,
  employees,
  readonly = false,
}: PunishmentWitnessTabProps) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold">Data Saksi & Validator</h3>
        <p className="text-gray-500 text-sm">Informasi pihak yang terlibat dalam proses hukuman</p>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Pemberi Hukuman</label>
          <AutocompleteInput
            value={formData.issued_by || ''}
            onChange={v => handleChange('issued_by', v)}
            placeholder="Ketik nama pemberi hukuman..."
            fieldName="issued_by"
            employees={employees}
            readonly={readonly}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Saksi</label>
          <MultiWitnessInput
            value={formData.witness || ''}
            onChange={v => handleChange('witness', v)}
            placeholder="Ketik nama saksi..."
            employees={employees}
            readonly={readonly}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Validator</label>
          <AutocompleteInput
            value={formData.validator || ''}
            onChange={v => handleChange('validator', v)}
            placeholder="Ketik nama validator..."
            fieldName="validator"
            employees={employees}
            readonly={readonly}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Catatan</label>
        <textarea
          value={formData.notes}
          onChange={e => handleChange('notes', e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
          placeholder="Catatan tambahan..."
          disabled={readonly}
        />
      </div>
    </div>
  );
}
